<script setup lang="ts">
import type { CmsElementProductListing } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { defu } from "defu";
import { computed, ref, useTemplateRef, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCategoryListing } from "#imports";
import type { Schemas, operations } from "#shopware";
import { useSidebarStore } from "@/stores/sidebar";
import { useHistoryNavigation } from '~/composables/useHistoryNavigation';
import { useScrollPosition } from '~/composables/useScrollPosition';
import { useUserAgent } from '~/composables/useUserAgent';
import { useGtmTracking } from '~/composables/useGtmTracking';

const { parsed } = useUserAgent();

const props = defineProps<{
  content: CmsElementProductListing;
}>();

const defaultLimit = 24;
const defaultPage = 1;
const defaultOrder = "custom_sorting";
// Template ref for optional scrollIntoView
const productListElement = useTemplateRef<HTMLElement | null>("productListElement");

type Translations = {
  listing: {
    noProducts: string;
    perPage: string;
    product: string;
    products: string;
  };
};
let translations: Translations = {
  listing: {
    noProducts: "No products found",
    perPage: "Per Page:",
    product: "Product",
    products: "Products",
  },
};
translations = defu(useCmsTranslations(), translations) as Translations;

// Use history navigation state (don't call provideHistoryNavigation - that's done in app.vue)
const { useHistoryState } = useHistoryNavigation()
const {  isHistoryBack } = useHistoryState()

const {
  changeCurrentPage,
  getCurrentPage,
  getElements,
  getTotalPagesCount,
  getSortingOrders,
  changeCurrentSortingOrder,
  getCurrentSortingOrder,
  loading,
  getTotal,
  getLimit,
  setInitialListing,
  loadMore
} = useCategoryListing();
const route = useRoute();
const router = useRouter();
const limit = ref(
  route.query.limit
    ? Number(route.query.limit)
    : props.content?.data?.listing?.limit
      ? Number(props.content?.data?.listing?.limit)
      : defaultLimit,
);

const initalRoute = defu(route);
// watch(
//   () => route,
//   (newRoute) => {
//     if (initalRoute.path !== newRoute.path) {
//       return;
//     }
//     if (Object.keys(newRoute.query).length > 0) {
//       return;
//     }
//     // this fires to reset the page when query are removed/empty on client side navigation for the same page (without hard reload)
//     changeCurrentPage(defaultPage, {
//       limit: defaultLimit,
//       p: defaultPage,
//       order: defaultOrder,
//     } as unknown as operations["searchPage post /search"]["body"]);
//   },
//   { deep: true },
// );

const isProductListing = computed(
  () => props.content?.type === "product-listing",
);
// This is a workaround because vercel caching with the nuxt preset does not support query params at the moment
// @see https://github.com/shopware/frontends/issues/687#issuecomment-1988392091
const compareRouteQueryWithInitialListing = async () => {
  const limitListing = props?.content?.data?.listing.limit ?? defaultLimit;
  const pageListing = props?.content?.data?.listing.page ?? defaultPage;
  const orderListing = props?.content?.data?.listing.sorting ?? defaultOrder;

  const isChangePageNeeded =
    (route.query.limit && limit.value !== limitListing) ||
    (route.query.p && Number(route.query.p) !== pageListing) ||
    (route.query.order && route.query.order !== orderListing);

  if (isChangePageNeeded) {
    const limitQuery = route.query.limit
      ? Number(route.query.limit)
      : defaultLimit;
    const pageQuery = route.query.p ? Number(route.query.p) : defaultPage;
    const orderQuery = route.query.order
      ? (route.query.order as string)
      : defaultOrder;
    const newQuery = {
      limit: limitQuery,
      p: pageQuery,
      order: orderQuery,
    };
    limit.value = limitQuery;
    await changeCurrentPage(
      pageQuery,
      newQuery as unknown as operations["searchPage post /search"]["body"],
    );
  }
};

// Seed filters/sorting from CMS data for SSR, but strip the product elements
// so the client always fetches the initial page from the real API (onMounted).
setInitialListing({
  ...(props?.content?.data?.listing as Schemas["ProductListingResult"]),
  elements: [],
});


const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || defaultOrder,
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        p: 1,
        order,
      },
    });

    await changeCurrentSortingOrder(order, {
      ...(route.query as unknown as operations["searchPage post /search"]["body"]),
      // Keep limit consistent with the listing composable (important after filter changes)
      limit: route.query.limit ? Number(route.query.limit) : getLimit.value,
    });
  },
});

const sidebarStore = useSidebarStore();

const openSidebar = () => {
  sidebarStore.openSidebar()
}

const isLoadingMore = ref(false);
const isRestoring = ref(false);
const hasInitialListingLoaded = ref(false);
const showInitialSkeleton = computed(
  () => !hasInitialListingLoaded.value || (loading.value && getElements.value.length === 0),
);
const showNoProductsState = computed(
  () => hasInitialListingLoaded.value && !loading.value && getElements.value.length === 0,
);

function getDesiredParamsFromRoute() {
  return {
    // Prefer the currently applied listing limit (may differ after filter changes).
    limit: route.query.limit ? Number(route.query.limit) : getLimit.value,
    // Ensure API always receives a valid sorting key.
    order: route.query.order
      ? String(route.query.order)
      : currentSortingOrder.value || defaultOrder,
    page: route.query.p ? Number(route.query.p) : defaultPage,
  } as { limit: number; order: string; page: number };
}


function buildPayload(page: number): Record<string, unknown> {
  const { limit: dLimit, order: dOrder } = getDesiredParamsFromRoute();
  return {
    // Spread all URL query params first so filter params (manufacturer, properties,
    // min-price, max-price, shipping-free, etc.) survive a page reload.
    // route.query is always kept in sync by SwProductListingFilters on every change.
    ...route.query,
    // Override pagination / sorting with the computed values so they are correct types.
    limit: dLimit,
    p: page,
    order: dOrder,
  } as Record<string, unknown>;
}

async function updateRouteQuery(page: number) {
  const { limit: dLimit } = getDesiredParamsFromRoute();
  await router.push({
    query: {
      ...route.query,
      p: page,
      limit: dLimit,
    },
  });
}

async function preloadPagesFromQuery() {
  const { page: targetPage } = getDesiredParamsFromRoute();
  if (!targetPage || targetPage <= 1) return;
  const maxPages = getTotalPagesCount.value || targetPage;
  const last = Math.min(targetPage, maxPages);
  // ensure page 1 matches desired params (without replacing SSR ordering logic)
  // await changeCurrentPage(1, buildPayload(1) as unknown as operations["searchPage post /search"]["body"]);
  for (let p = 2; p <= last; p++) {
    await loadMore(buildPayload(p) as unknown as operations["searchPage post /search"]["body"]);
  }
}

// Create a loadMoreProducts function to handle the load more behavior
const loadMoreProducts = async () => {
  if (isLoadingMore.value || isRestoring.value) return;
  isLoadingMore.value = true;
  try {
    const nextPage = getCurrentPage.value + 1;
    await loadMore(buildPayload(nextPage) as unknown as operations["searchPage post /search"]["body"]);
    await updateRouteQuery(nextPage);
  } catch (error) {
    console.error('Error loading more products:', error);
  } finally {
    isLoadingMore.value = false;
  }
};

const getStaggeredStyle = (index: number) => {
  return {
    '--index': index,
  };
};

const isMounted = ref(false);
// Track if we're coming from history navigation
const isFromHistoryNavigation = ref(false);

const { trackViewItemList } = useGtmTracking();

watch(
  [hasInitialListingLoaded, getElements],
  ([loaded, elements]) => {
    if (!loaded || !elements?.length) return;
    trackViewItemList(elements as Schemas["Product"][], {
      listName: route.meta?.pageTitle as string || document.title || route.path,
      listId: route.path,
    });
  },
  { immediate: false },
);

// Initialize common back-to-scroll behavior via composable
const { isBackToHistory, initScrollPosition, cleanup: cleanupScrollPosition } = useScrollPosition({
  pageId: 'product-listing',
  trackSelector: 'li[id^="pl-"]',
  restoreOnHistory: true,
  scrollBehavior: 'auto',
  blockPosition: 'center'
});

onMounted(() => {
  // Check if this is a history navigation
  isFromHistoryNavigation.value = isHistoryBack.value;
  
  // Only trigger animation if not coming from history navigation
  if (!isFromHistoryNavigation.value) {
    setTimeout(() => {
      isMounted.value = true;
    }, 400);
  } else {
    // Skip animation for history navigation by setting isMounted immediately
    isMounted.value = true;
  }
});


const restoreListingInBackground = async () => {
  try {
    isRestoring.value = true;
    await changeCurrentPage(1, buildPayload(1) as unknown as operations["searchPage post /search"]["body"]);

    // Append pages 2…N when restoring a paginated scroll state
    if (route.query.p && Number(route.query.p) > 1) {
      await preloadPagesFromQuery();
    }
  } finally {
    isRestoring.value = false;
    hasInitialListingLoaded.value = true;
  }
};

onMounted(() => {
  // Initialize back-to-scroll on the listing container
  if (productListElement.value) {
    const stop = initScrollPosition(productListElement.value as unknown as HTMLElement);
    onBeforeUnmount(() => {
      stop();
    });
  }

  // Disable default scroll behavior immediately
  if (typeof window !== 'undefined') {
    // Prevent any default scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }

  // Run listing restore asynchronously to avoid blocking initial paint.
  void restoreListingInBackground();
});

onBeforeUnmount(() => {
  // Cleanup shared scroll position logic
  cleanupScrollPosition();
  if (typeof window !== 'undefined') {
    // Re-enable default scroll behavior
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'auto';
    }
  }
});
</script>

<template>
  <div class="product-listing product-listing-section w-full h-full" :aria-label="$t('productListing.ariaLabelProductList')" ref="productListElement">
    <div class="title lg:flex justify-between items-center">
      <h2 v-if="getTotal > 0" class="heading text-2xl lg:text-6xl 3xl:text-10xl font-marcellus text-grey-700 leading-[30px] md:leading-none">{{ getTotal }} {{ $t('productListing.results') }}</h2>
      <div v-if="getTotal > 0" class="flex items-center justify-between gap-3 mt-5 lg:mt-0">
        <!-- Filter button - hidden in mobile screens -->
        <button
          class="flex justify-center items-center gap-3 lg:hidden w-full max-w-[192px] min-w-[100px] md:min-w-44 lg:min-w-56 h-[40px] lg:h-[44px] border border-grey-500 rounded-full px-6 md:px-4 py-1.5 md:py-2 focus:outline-none appearance-none cursor-pointer text-base lg:text-base text-grey-700 font-mulish font-normal leading-none"
          :aria-label="$t('productListing.ariaLabelToggleSidebar')"
          id="toggleSidebar"
          @click="openSidebar()">
          <i class="icon-filter text-md 2xl:text-xl 3xl:text-2xl" aria-hidden="true"></i>
          <span class="whitespace-nowrap">{{ $t('productListing.filter') }}</span>
        </button>

        <!-- Sorting options -->
        <div class="sorting flex items-center">
          <label
            for="sorting"
            class="font-mullish font-normal text-base text-grey-700 mr-3.5 hidden md:inline-block"
            >{{ $t('productListing.sort') }}:</label
          >
          <div class="sorting-select relative">
            <select
              name="sorting"
              id="sorting"
              :aria-label="$t('productListing.sort')"
              @change="
                currentSortingOrder = ($event.target as HTMLSelectElement).value
              "
              class="form-control bg-transparent text-center lg:text-left min-w-[100px] md:min-w-44 lg:min-w-56 h-[40px] lg:h-[44px] border border-grey-500 rounded-full px-6 py-1.5 md:py-2 pr-10 focus:outline-none appearance-none cursor-pointer text-base lg:text-base text-grey-700 font-mulish font-normal leading-5">
              <template v-for="sorting in getSortingOrders" :key="sorting.key">
                <option
                  :value="sorting.key"
                  :selected="sorting.key === getCurrentSortingOrder"
                >
                  {{ sorting.label }}
                </option>
              </template>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 mt-[1px]">
              <span
                class="icon-caret-back text-xxs text-grey-700"
                aria-colspan="true"
                aria-hidden="true"></span>
            </div>  
          </div>
        </div>
      </div>
    </div>
    <div class="content mt-6 lg:mt-10">
      <ul class="grid grid-rows-auto grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-4 md:gap-x-4 gap-y-14" 
      :class="{ 'tall:grid-cols-3 tall:gap-x-6': !parsed.isTablet }"
      v-if="getElements.length > 0 || showInitialSkeleton">
        <template v-if="!showInitialSkeleton">
          <li v-for="(product, index) in getElements" :key="product.id" class="snap-center-disable" :id="`pl-${product.id}`">
            <ProductCard
              v-if="product"
              :product="product"
              :is-product-listing="isProductListing"
              :class="[{ 'fade-in-up staggered-item' : !isBackToHistory, 'is-mounted': isMounted, 'no-animation': isFromHistoryNavigation }]"
              :style="getStaggeredStyle(index)"
              :is-listing-page="true"
            />
          </li>
        </template>
        <template v-if="showInitialSkeleton">
          <SkeletonsProductCard
            v-for="index in limit"
            :key="index"
            class="w-full sm:w-3/7 lg:w-2/7 2xl:w-7/24 mr-0 sm:mr-8 mb-8"
          />
        </template>
      </ul>
      <div v-else-if="showNoProductsState" class="flex flex-col items-center justify-center py-6 px-4">
        <div class="w-24 h-24 md:w-48 md:h-28 mb-0 md:mb-4 text-grey-400 flex items-center justify-center text-[60px] md:text-[100px]">
          <i class="icon-search-outline" aria-hidden="true"></i>
        </div>
        <h2 class="text-2xl md:text-3xl font-marcellus text-grey-700 mb-4">
          {{ $t('listing.noProducts') }}
        </h2>
        <p class="text-base md:text-lg text-grey-500 text-center mb-8">
          {{ $t('productListing.tryAdjustingFilters') }}
        </p>
      </div>

      <div class="text-center mt-7 lg:mt-10">
        <button
          v-if="getCurrentPage < getTotalPagesCount"
          @click="loadMoreProducts"
          :disabled="isLoadingMore || loading"
          class="btn btn-primary btn-icon-end"
        >
          <span class="my-auto" v-if="!isLoadingMore && !loading">{{ $t("productListing.loadMore") }}</span>
          <span class="my-auto" v-if="isLoadingMore || loading">{{ $t("productListing.loading") }}</span>
          <i v-if="!isLoadingMore && !loading" class="icon-refresh !text-md font-medium leading-none" aria-hidden="true"></i>
          <i v-if="isLoadingMore || loading" class="icon-refresh !text-md font-medium leading-none animate-spin [animation-duration:500ms]"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-in-up {
  opacity: 0;
  transform: translateY(20px);
}

.fade-in-up.is-mounted {
  animation: fadeInUp 0.5s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.staggered-item.is-mounted {
  animation-delay: calc(var(--index) * 50ms);
}

/* Skip animation for history navigation */
.no-animation {
  opacity: 1 !important;
  transform: translateY(0) !important;
  animation: none !important;
}
</style>
